import { Injectable, OnDestroy } from '@angular/core';
import { ConfigService } from './config.service';
import { GuacamoleClientInstance } from './guacamole-client-instance';
import { VmSessionManagerService, VmSessionData } from './vm-session-manager.service';
import { SharedConnectionResponse } from '../models/shared-connection.model';
import { ScreenDimensionsService } from './screen-dimensions.service';
import { Log, IShortLogger } from './logger.service';

/**
 * Singleton service for managing multiple Guacamole client instances.
 *
 * This service implements the Manager pattern to handle multiple concurrent VM connections.
 * It enables true multi-window support where trainers can access multiple students' VMs
 * simultaneously without session conflicts.
 *
 * Key features:
 * - Factory methods for creating client instances
 * - Active client tracking with composite keys
 * - Global cleanup and resource management
 * - Integration with VmSessionManagerService for session data
 * - Support for both regular user sessions and trainer remote access
 *
 * Architecture:
 * - Each VM window gets its own GuacamoleClientInstance
 * - Instances are tracked using {classId}/{userId} composite keys
 * - Automatic cleanup prevents memory leaks
 * - Service destruction handles global cleanup
 *
 * @class GuacamoleClientManagerService
 *
 * @example
 * ```typescript
 * // Inject in component
 * constructor(private guacamoleManager: GuacamoleClientManagerService) {}
 *
 * // Create client instance for VM window
 * const instance = this.guacamoleManager.createClient(
 *   'class-123', 'user-456', displayElement
 * );
 *
 * // Initialize with session data
 * await instance.initialize(sessionData);
 *
 * // Later cleanup (e.g., in ngOnDestroy)
 * this.guacamoleManager.removeClient('class-123', 'user-456');
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class GuacamoleClientManagerService implements OnDestroy {
  private readonly logger: IShortLogger<GuacamoleClientManagerService> = Log.create(GuacamoleClientManagerService);

  /**
   * Map of active client instances keyed by session identifier.
   * Key format: "{classId}/{userId}"
   * @private
   * @type {Map<string, GuacamoleClientInstance>}
   */
  private activeClients: Map<string, GuacamoleClientInstance> = new Map<string, GuacamoleClientInstance>();

  /**
   * Creates an instance of GuacamoleClientManagerService.
   *
   * @param configService - Service for getting configuration
   * @param vmSessionManager - Service for session data management
   * @param screenDimensionsService - Service for optimal screen dimension calculation
   */
  constructor(
    private configService: ConfigService,
    private vmSessionManager: VmSessionManagerService,
    private screenDimensionsService: ScreenDimensionsService
  ) {
    this.logger.d('GuacamoleClientManagerService initialized');
  }

  /**
   * Create a new Guacamole client instance for the specified class/user combination.
   * If a client already exists for this combination, it will be cleaned up first.
   *
   * @param classId - The class identifier
   * @param userId - The user identifier (current user or target user for trainers)
   * @param displayElement - HTML element to attach the Guacamole display
   * @returns New GuacamoleClientInstance ready for initialization
   *
   * @example
   * ```typescript
   * const displayElement = this.displayContainer.nativeElement;
   * const client = this.guacamoleManager.createClient(
   *   'class-123', 'user-456', displayElement
   * );
   * ```
   */
  createClient(classId: string, userId: string, displayElement: HTMLElement): GuacamoleClientInstance {
    const sessionKey = this.vmSessionManager.generateSessionKey(classId, userId);

    // Clean up existing client if any
    if (this.activeClients.has(sessionKey)) {
      this.logger.d(`Existing client found for ${sessionKey}, cleaning up first`);
      this.removeClient(classId, userId);
    }

    const clientInstance = new GuacamoleClientInstance(
      sessionKey,
      displayElement,
      this.configService,
      this.screenDimensionsService
    );

    this.activeClients.set(sessionKey, clientInstance);
    this.logger.d(`Created Guacamole client for session: ${sessionKey}`);

    return clientInstance;
  }

  /**
   * Get an existing client instance by class and user ID.
   *
   * @param classId - The class identifier
   * @param userId - The user identifier
   * @returns GuacamoleClientInstance if found, undefined otherwise
   *
   * @example
   * ```typescript
   * const client = this.guacamoleManager.getClient('class-123', 'user-456');
   * if (client && client.isClientConnected()) {
   *   this.logger.d('Client is connected');
   * }
   * ```
   */
  getClient(classId: string, userId: string): GuacamoleClientInstance | undefined {
    const sessionKey = this.vmSessionManager.generateSessionKey(classId, userId);
    return this.activeClients.get(sessionKey);
  }

  /**
   * Remove and cleanup a client instance.
   *
   * @param classId - The class identifier
   * @param userId - The user identifier
   *
   * @example
   * ```typescript
   * // In ngOnDestroy
   * this.guacamoleManager.removeClient(this.classId, this.userId);
   * ```
   */
  removeClient(classId: string, userId: string): void {
    const sessionKey = this.vmSessionManager.generateSessionKey(classId, userId);
    const client = this.activeClients.get(sessionKey);

    if (client) {
      client.cleanup();
      this.activeClients.delete(sessionKey);
      this.logger.d(`Removed Guacamole client for session: ${sessionKey}`);
    } else {
      this.logger.w(`No Guacamole client found to remove for session: ${sessionKey}`);
    }
  }

  /**
   * Check if a client exists for the specified class/user combination.
   *
   * @param classId - The class identifier
   * @param userId - The user identifier
   * @returns True if client exists, false otherwise
   *
   * @example
   * ```typescript
   * if (this.guacamoleManager.hasClient('class-123', 'user-456')) {
   *   this.logger.d('Client already exists');
   * }
   * ```
   */
  hasClient(classId: string, userId: string): boolean {
    const sessionKey = this.vmSessionManager.generateSessionKey(classId, userId);
    return this.activeClients.has(sessionKey);
  }

  /**
   * Create and initialize a client in one operation.
   * Convenience method that combines createClient and initialize.
   *
   * @param classId - The class identifier
   * @param userId - The user identifier
   * @param displayElement - HTML element to attach the Guacamole display
   * @returns Promise that resolves when client is initialized
   * @throws Error if session data not found or initialization fails
   *
   * @example
   * ```typescript
   * try {
   *   await this.guacamoleManager.createAndInitializeClient(
   *     'class-123', 'user-456', displayElement
   *   );
   *   this.logger.d('Client ready');
   * } catch (error) {
   *   this.logger.e('Failed to initialize client:', error);
   * }
   * ```
   */
  async createAndInitializeClient(
    classId: string,
    userId: string,
    displayElement: HTMLElement
  ): Promise<GuacamoleClientInstance> {
    // Get session data from cache
    const sessionData = this.vmSessionManager.getSession(classId, userId);
    if (!sessionData) {
      throw new Error(`No session data found for ${classId}/${userId}`);
    }

    // Create client instance
    const client = this.createClient(classId, userId, displayElement);

    // Initialize with session data
    await client.initialize(sessionData);

    return client;
  }

  /**
   * Create and initialize a client using provided session data directly —
   * does NOT read from localStorage.  Use this when the child window has
   * fetched credentials from the backend itself at startup.
   *
   * @param classId - The class identifier
   * @param userId - The user identifier
   * @param sessionData - Session data already fetched from the backend
   * @param displayElement - HTML element to attach the Guacamole display
   * @returns Promise that resolves when client is initialized
   */
  async createAndInitializeClientWithData(
    classId: string,
    userId: string,
    sessionData: VmSessionData,
    displayElement: HTMLElement
  ): Promise<GuacamoleClientInstance> {
    const client = this.createClient(classId, userId, displayElement);
    await client.initialize(sessionData);
    return client;
  }

  /**
   * Create and initialize a shared connection for trainer to join student's session.
   * This method creates a secondary Guacamole connection that multiplexes into
   * an existing student session.
   *
   * @param sharedConnectionInfo - Shared connection information from backend
   * @param studentClassId - Student's class ID
   * @param studentUserId - Student's user ID
   * @param trainerUserId - Trainer's user ID (for tracking)
   * @param displayElement - HTML element to attach the Guacamole display
   * @returns Promise that resolves when shared client is initialized
   * @throws Error if session data not found or initialization fails
   *
   * @example
   * ```typescript
   * const sharedInfo = await connectionSharingService.getSharedConnection({
   *   classId: 'class-123',
   *   studentUserId: 'student-456',
   *   trainerUserId: 'trainer-789'
   * });
   *
   * const client = await this.guacamoleManager.createAndInitializeSharedClient(
   *   sharedInfo, 'class-123', 'student-456', 'trainer-789', displayElement
   * );
   * ```
   */
  async createAndInitializeSharedClient(
    sharedConnectionInfo: SharedConnectionResponse,
    studentClassId: string,
    studentUserId: string,
    trainerUserId: string,
    displayElement: HTMLElement
  ): Promise<GuacamoleClientInstance> {
    // Get student's session data (we need protocol and other connection params)
    const sessionData = this.vmSessionManager.getSession(studentClassId, studentUserId);
    if (!sessionData) {
      throw new Error(`No session data found for student ${studentClassId}/${studentUserId}`);
    }

    // Create client instance with trainer's identifier for tracking
    // Use a special session key format to track this as a trainer-joined session
    const trainerSessionKey = `${studentClassId}/${studentUserId}/trainer-${trainerUserId}`;
    const sessionKeyForManager = this.vmSessionManager.generateSessionKey(studentClassId, `${studentUserId}-trainer-${trainerUserId}`);

    // Remove existing client if any
    if (this.activeClients.has(sessionKeyForManager)) {
      this.logger.d(`Existing shared client found for ${sessionKeyForManager}, cleaning up first`);
      const existingClient = this.activeClients.get(sessionKeyForManager);
      existingClient?.cleanup();
      this.activeClients.delete(sessionKeyForManager);
    }

    const clientInstance = new GuacamoleClientInstance(
      trainerSessionKey,
      displayElement,
      this.configService,
      this.screenDimensionsService
    );

    this.activeClients.set(sessionKeyForManager, clientInstance);
    this.logger.d(`Created shared Guacamole client for trainer session: ${trainerSessionKey}`);

    // Initialize with shared connection info
    await clientInstance.initializeShared(sharedConnectionInfo, sessionData);

    return clientInstance;
  }


  /**
   * Get all active client session keys.
   * Useful for debugging and monitoring.
   *
   * @returns Array of session keys for all active clients
   *
   * @example
   * ```typescript
   * const activeKeys = this.guacamoleManager.getActiveClientKeys();
   * this.logger.d('Active clients:', activeKeys);
   * ```
   */
  getActiveClientKeys(): string[] {
    return Array.from(this.activeClients.keys());
  }

  /**
   * Get the number of currently active clients.
   *
   * @returns Number of active clients
   *
   * @example
   * ```typescript
   * const count = this.guacamoleManager.getActiveClientCount();
   * this.logger.d(`${count} active VM connections`);
   * ```
   */
  getActiveClientCount(): number {
    return this.activeClients.size;
  }

  /**
   * Get all active clients for a specific class.
   * Useful for trainers to see all active student sessions.
   *
   * @param classId - The class identifier
   * @returns Array of objects containing userId and client instance
   *
   * @example
   * ```typescript
   * const classClients = this.guacamoleManager.getClientsForClass('class-123');
   * classClients.forEach(({userId, client}) => {
   *   this.logger.d(`Student ${userId} is ${client.isClientConnected() ? 'connected' : 'disconnected'}`);
   * });
   * ```
   */
  getClientsForClass(classId: string): Array<{userId: string, client: GuacamoleClientInstance}> {
    const classClients: Array<{userId: string, client: GuacamoleClientInstance}> = [];

    for (const [sessionKey, client] of this.activeClients.entries()) {
      if (sessionKey.startsWith(`${classId}-`)) {
        const userId = sessionKey.split('-')[1];
        classClients.push({ userId, client });
      }
    }

    return classClients;
  }

  /**
   * Remove all clients for a specific class.
   * Useful when a class ends or needs cleanup.
   *
   * @param classId - The class identifier
   * @returns Number of clients removed
   *
   * @example
   * ```typescript
   * const removedCount = this.guacamoleManager.removeClientsForClass('class-123');
   * this.logger.d(`Removed ${removedCount} clients for class`);
   * ```
   */
  removeClientsForClass(classId: string): number {
    let removedCount = 0;

    for (const [sessionKey, client] of this.activeClients.entries()) {
      if (sessionKey.startsWith(`${classId}/`)) {
        client.cleanup();
        this.activeClients.delete(sessionKey);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      this.logger.i(`Removed ${removedCount} Guacamole clients for class: ${classId}`);
    }

    return removedCount;
  }

  /**
   * Clean up all active clients.
   * This removes all VM connections and frees resources.
   *
   * @example
   * ```typescript
   * this.guacamoleManager.cleanupAllClients();
   * ```
   */
  cleanupAllClients(): void {
    this.logger.i('Cleaning up all Guacamole clients');

    for (const [sessionKey, client] of this.activeClients) {
      try {
        client.cleanup();
      } catch (error) {
        this.logger.e(`Error cleaning up client ${sessionKey}:`, error);
      }
    }

    this.activeClients.clear();
  }

  /**
   * Get connection status for all clients.
   * Returns a summary of connected vs disconnected clients.
   *
   * @returns Object with connection statistics
   *
   * @example
   * ```typescript
   * const status = this.guacamoleManager.getConnectionStatus();
   * this.logger.d(`${status.connected} connected, ${status.disconnected} disconnected`);
   * ```
   */
  getConnectionStatus(): {total: number, connected: number, disconnected: number} {
    let connected = 0;
    let disconnected = 0;

    for (const client of this.activeClients.values()) {
      if (client.isClientConnected()) {
        connected++;
      } else {
        disconnected++;
      }
    }

    return {
      total: this.activeClients.size,
      connected,
      disconnected
    };
  }

  /**
   * Upload a file to the VM session identified by classId and userId.
   *
   * Delegates to {@link GuacamoleClientInstance#uploadFile}. Returns false if
   * no active client is found for the session.
   *
   * @param classId - The class identifier
   * @param userId - The user identifier
   * @param file - The browser {@link File} to send to the VM
   * @returns true if the upload was dispatched, false if the session was not found
   */
  uploadFileToSession(classId: string, userId: string, file: File): boolean {
    const client = this.getClient(classId, userId);
    if (!client) {
      this.logger.w(`uploadFileToSession: no client found for ${classId}/${userId}`);
      return false;
    }
    client.uploadFile(file);
    return true;
  }

  /**
   * Angular lifecycle hook - cleanup when service is destroyed.
   * Automatically cleans up all clients to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.logger.d('GuacamoleClientManagerService destroying, cleaning up all clients');
    this.cleanupAllClients();
  }
}
